import { InteractionRequiredAuthError } from "@azure/msal-browser";
import axios from "axios";
import { loginRequest } from "../../config";
import { Env } from "../constants/env";
import { ResponseCode } from "../constants/responseCode";

export const getAccessToken = () => {
  return localStorage.getItem("token");
};


export const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

export const getAuthType = (
  token: string | null
): "manual" | "microsoft" | null => {
  if (!token) return null;

  const decoded = decodeJWT(token);
  if (!decoded || !decoded.iss) return null;

  if (decoded.iss === "manual-auth") {
    return "manual";
  } else if (decoded.iss && decoded.iss.includes("microsoft")) {
    return "microsoft";
  }

  return null;
};

export const getAuthProvider = (): "manual" | "microsoft" | null => {
  const provider = localStorage.getItem("authProvider");
  if (provider === "manual" || provider === "microsoft") return provider;
  return getAuthType(localStorage.getItem("idToken"));
};

export const debounceFn = (func: (searchKey: any) => void, delay: number) => {
  let timer: NodeJS.Timeout;
  return function innerFn(this: any, ...args: any) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

export const debounce = (func: () => void, delay: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
};

export const handleSilentLogout = (instance: any) => {
  const authProvider = getAuthProvider();

  // If it's Microsoft auth and instance is available, use MSAL logout
  if (authProvider === "microsoft" && instance) {
    // Set flag to prevent login page flash
    sessionStorage.setItem("msal_logout_in_progress", "true");

    instance
      .logout({
        onRedirectNavigate: () => false,
      })
      .then(() => {
        // Clear storage after logout
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/#/";
      })
      .catch(() => {
        // If logout fails, clear and redirect manually
        sessionStorage.removeItem("msal_logout_in_progress");
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/#/";
      });
  } else {
    // For manual auth or if no instance, clear and redirect
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/#/";
  }
};

/**
 * Exchanges a Microsoft idToken for a backend JWT and stores it as idToken.
 */
export const exchangeAndStoreBackendToken = async (
  microsoftIdToken: string
): Promise<string | null> => {
  try {
    const response = await axios({
      method: "POST",
      url: `${Env.BASE_URL}/auth/token`,
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${microsoftIdToken}`,
      },
      transformRequest: [(data, headers) => {
        if (headers) {
          delete headers["Content-Type"];
          delete headers["content-type"];
        }
        return data;
      }],
    });

    const payload = response?.data;
    const backendToken = payload?.data;

    if (
      payload?.statusCode === ResponseCode.SUCCESS &&
      payload?.success &&
      backendToken
    ) {
      localStorage.setItem("idToken", backendToken);
      localStorage.setItem("authProvider", "microsoft");
      return backendToken;
    }

    console.error("Failed to exchange Microsoft token:", payload?.messages);
    return null;
  } catch (error) {
    console.error("Error exchanging Microsoft token:", error);
    return null;
  }
};

export const needsMicrosoftTokenExchange = (): boolean => {
  const idToken = localStorage.getItem("idToken");
  const authProvider = localStorage.getItem("authProvider");
  const authType = getAuthType(idToken);

  if (idToken && authProvider === "microsoft" && authType === "manual") {
    return false;
  }

  return true;
};

export const silentTokenAcquisition = async (instance: any, account: any) => {
  try {
    if (!needsMicrosoftTokenExchange()) {
      return localStorage.getItem("idToken");
    }

    const response = await instance.acquireTokenSilent({
      account,
      scopes: loginRequest.scopes,
    });
    if (response?.idToken) {
      return await exchangeAndStoreBackendToken(response.idToken);
    }
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      try {
        const response = await instance.acquireTokenPopup({
          account,
          scopes: loginRequest.scopes,
        });
        if (response?.idToken) {
          return await exchangeAndStoreBackendToken(response.idToken);
        }
      } catch (popupError) {
        console.error("Failed to acquire token via popup:", popupError);
        handleSilentLogout(instance);
        return null;
      }
    } else {
      handleSilentLogout(instance);
      return null;
    }
  }
  return null;
};
